interface DatasetInfo {
  datasetId: string;
  nameInWebmap: string;
  fieldCollected: number;
  collectionMethod: string; // "Aerial", "handheld", "Cart"
  sensorType: string;
  sensor: string;
  platform: string;
  hasRaster: boolean;   // True if dataset has imagery (raster data)
  hasVector: boolean;   // True if dataset has landmine detections (vector data)
  addedToWebmap: boolean;
  group: string;
  dateCollected: string;
  agl: number | string; // AGL (m) - can be number or empty string
  burialTime: string; // "Preburial" or "Postburial"
}

export interface DatasetsData {
  [datasetId: string]: DatasetInfo;
}

/**
 * Parses the consolidated datasets.csv into a structured object organized by dataset ID
 * @param csvContent - The raw CSV content as a string
 * @returns DatasetsData object organized by dataset ID
 */
export function parseDatasets(csvContent: string): DatasetsData {
  const lines = csvContent.trim().split('\n');
  if (lines.length < 2) {
    throw new Error('CSV must contain at least a header and one data row');
  }

  const result: DatasetsData = {};
  
  // Parse header to find column indices
  const headers = lines[0].split(',').map(h => h.trim());
  const getColumnIndex = (columnName: string) => {
    const index = headers.findIndex(h => h === columnName);
    if (index === -1) {
      throw new Error(`Required column not found: ${columnName}`);
    }
    return index;
  };

  const indices = {
    datasetId: getColumnIndex('Dataset ID'),
    nameInWebmap: getColumnIndex('Name in Webmap'),
    fieldCollected: getColumnIndex('Field Collected'),
    collectionMethod: getColumnIndex('Collection Method'),
    sensorType: getColumnIndex('Sensor Type'),
    sensor: getColumnIndex('Sensor'),
    platform: getColumnIndex('Platform'),
    raster: getColumnIndex('Raster'),
    vector: getColumnIndex('Vector'),
    addedToWebmap: getColumnIndex('Added to webmap'),
    group: getColumnIndex('Group'),
    dateCollected: getColumnIndex('Date Collected'),
    yearCollected: getColumnIndex('Year Collected'),
    agl: getColumnIndex('AGL (m)'),
    burialTime: getColumnIndex('Burial Time')
  };

  // Process data rows (skip header row)
  for (let i = 1; i < lines.length; i++) {
    const row = lines[i].split(',').map(cell => cell.trim());
    
    // Skip empty rows or rows without Dataset ID
    if (!row[indices.datasetId] || row[indices.datasetId] === '') continue;
    
    const datasetId = row[indices.datasetId];
    const nameInWebmap = row[indices.nameInWebmap] || '';
    const fieldCollectedStr = row[indices.fieldCollected] || '';
    const fieldCollected = fieldCollectedStr.includes('Field') ? 
      parseInt(fieldCollectedStr.replace('Field ', '')) : 
      parseInt(fieldCollectedStr) || 0;
    const collectionMethod = row[indices.collectionMethod] || '';
    const sensorType = row[indices.sensorType] || '';
    const sensor = row[indices.sensor] || '';
    const platform = row[indices.platform] || '';
    
    // Parse raster and vector availability
    const rasterValue = (row[indices.raster] || '').toLowerCase();
    const vectorValue = (row[indices.vector] || '').toLowerCase();
    const addedToWebmapValue = (row[indices.addedToWebmap] || '').toLowerCase();
    
    const hasRaster = rasterValue === 'uploaded' || rasterValue === 'yes';
    const hasVector = vectorValue === 'uploaded' || vectorValue === 'yes';
    const addedToWebmap = addedToWebmapValue === 'uploaded' || addedToWebmapValue === 'yes';
    
    const group = row[indices.group] || '';
    const dateCollected = row[indices.dateCollected] || '';
    const yearCollected = row[indices.yearCollected] || '';
    
    // Combine date and year for display, removing quotes if present
    const fullDate = dateCollected && yearCollected 
      ? `${dateCollected}, ${yearCollected}`
      : dateCollected || yearCollected || '';
    const cleanDate = fullDate.replace(/"/g, ''); // Remove any quotes
    
    const aglValue = row[indices.agl] || '';
    const agl = aglValue === '' ? '' : parseFloat(aglValue);
    const burialTime = row[indices.burialTime] || '';

    result[datasetId] = {
      datasetId,
      nameInWebmap,
      fieldCollected,
      collectionMethod,
      sensorType,
      sensor,
      platform,
      hasRaster,
      hasVector,
      addedToWebmap,
      group,
      dateCollected: cleanDate,
      agl,
      burialTime
    };
  }

  return result;
}

/**
 * Helper function to get dataset info for a specific dataset ID
 * @param data - Parsed DatasetsData
 * @param datasetId - Dataset ID to get info for
 * @returns DatasetInfo or undefined if not found
 */
export function getDatasetInfo(data: DatasetsData, datasetId: string): DatasetInfo | undefined {
  return data[datasetId];
}

/**
 * Helper function to check if a dataset has raster data (imagery)
 * @param data - Parsed DatasetsData
 * @param datasetId - Dataset ID to check
 * @returns True if dataset has raster data
 */
export function hasRasterData(data: DatasetsData, datasetId: string): boolean {
  return data[datasetId]?.hasRaster || false;
}

/**
 * Helper function to check if a dataset has vector data (landmine detections)
 * @param data - Parsed DatasetsData
 * @param datasetId - Dataset ID to check
 * @returns True if dataset has vector data
 */
export function hasVectorData(data: DatasetsData, datasetId: string): boolean {
  return data[datasetId]?.hasVector || false;
}

/**
 * Helper function to get all dataset IDs for a specific field
 * @param data - Parsed DatasetsData
 * @param fieldNumber - Field number to filter by
 * @returns Array of dataset IDs for the specified field
 */
export function getDatasetsByField(data: DatasetsData, fieldNumber: number): string[] {
  return Object.keys(data).filter(datasetId => 
    data[datasetId].fieldCollected === fieldNumber
  );
}

/**
 * Helper function to get all dataset IDs for a specific sensor type
 * @param data - Parsed DatasetsData
 * @param sensorType - Sensor type to filter by
 * @returns Array of dataset IDs for the specified sensor type
 */
export function getDatasetsBySensorType(data: DatasetsData, sensorType: string): string[] {
  return Object.keys(data).filter(datasetId => 
    data[datasetId].sensorType.toLowerCase().includes(sensorType.toLowerCase())
  );
}

/**
 * Helper function to get all dataset IDs that have both raster and vector data
 * @param data - Parsed DatasetsData
 * @returns Array of dataset IDs with both data types
 */
export function getDatasetsWithBothTypes(data: DatasetsData): string[] {
  return Object.keys(data).filter(datasetId => 
    data[datasetId].hasRaster && data[datasetId].hasVector
  );
}

/**
 * Helper function to get all dataset IDs that have only vector data (detections only)
 * @param data - Parsed DatasetsData
 * @returns Array of dataset IDs with only vector data
 */
export function getDatasetsWithVectorOnly(data: DatasetsData): string[] {
  return Object.keys(data).filter(datasetId => 
    !data[datasetId].hasRaster && data[datasetId].hasVector
  );
}

/**
 * Helper function to get all dataset IDs that have only raster data (imagery only)
 * @param data - Parsed DatasetsData
 * @returns Array of dataset IDs with only raster data
 */
export function getDatasetsWithRasterOnly(data: DatasetsData): string[] {
  return Object.keys(data).filter(datasetId => 
    data[datasetId].hasRaster && !data[datasetId].hasVector
  );
}

/**
 * Helper function to get datasets by collection method
 * @param data - Parsed DatasetsData
 * @param method - Collection method ("Aerial", "handheld", "Cart")
 * @returns Array of dataset IDs for the specified collection method
 */
export function getDatasetsByCollectionMethod(data: DatasetsData, method: string): string[] {
  return Object.keys(data).filter(datasetId => 
    data[datasetId].collectionMethod.toLowerCase().includes(method.toLowerCase())
  );
}

/**
 * Helper function to get datasets by burial time
 * @param data - Parsed DatasetsData
 * @param burialTime - Burial time ("Preburial" or "Postburial")
 * @returns Array of dataset IDs for the specified burial time
 */
export function getDatasetsByBurialTime(data: DatasetsData, burialTime: string): string[] {
  return Object.keys(data).filter(datasetId => 
    data[datasetId].burialTime.toLowerCase() === burialTime.toLowerCase()
  );
}

/**
 * Helper function to get all available dataset IDs (sorted)
 * @param data - Parsed DatasetsData
 * @returns Array of all dataset IDs
 */
export function getAllDatasetIds(data: DatasetsData): string[] {
  return Object.keys(data).sort((a, b) => {
    // Custom sort to handle numeric and alphanumeric dataset IDs properly
    const aMatch = a.match(/^(\d+)(?:-(\d+))?$/);
    const bMatch = b.match(/^(\d+)(?:-(\d+))?$/);
    
    if (aMatch && bMatch) {
      const aMain = parseInt(aMatch[1]);
      const bMain = parseInt(bMatch[1]);
      if (aMain !== bMain) return aMain - bMain;
      
      const aSub = aMatch[2] ? parseInt(aMatch[2]) : 0;
      const bSub = bMatch[2] ? parseInt(bMatch[2]) : 0;
      return aSub - bSub;
    }
    
    return a.localeCompare(b);
  });
}

// Example usage:
// const csvContent = await fetch('/data/datasets.csv').then(r => r.text());
// const parsedData = parseDatasets(csvContent);
// 
// // Get complete dataset info
// const dataset31 = getDatasetInfo(parsedData, '3-1');
// console.log(dataset31?.sensorType); // "RGB camera"
// console.log(dataset31?.hasRaster);  // true
// console.log(dataset31?.hasVector);  // true
// 
// // Filter datasets
// const field1Datasets = getDatasetsByField(parsedData, 1);
// const aerialDatasets = getDatasetsByCollectionMethod(parsedData, 'Aerial');
// const rgbDatasets = getDatasetsBySensorType(parsedData, 'RGB camera');